import React, { useEffect, useRef, useState } from "react";
import { CommonGrid } from "styledComponents/common";
import {
  formatDelta,
  getCurrentAndLastMonthYear,
  getDeltaType,
} from "utils/helper";

import { DashBoardCommonGrid } from "../../DashBoardNew/dashBoardNewStyles";
import CompleteJobCompareChart from "./CompleteJobCompareChart";
import SmallInfoCard from "./SmallInfoCard";

const DashBoardOperationThirdSection = ({
  getDashboardCompleteJobCompare,
  completeJobCompareIsLoading,
  lastYearCompleteJobCompare,
  thisYearCompleteJobCompare,
  currentMonthIndex,
  selectedOrganization,
  getDashboardOrganizationJobs,
  organizationJobsIsLoading,
  organizationJobsData,
}) => {
  const [cardsData, setCardsData] = useState([]);
  const [maxCardHeight, setMaxCardHeight] = useState(0);
  const cardRefs = useRef([]);

  const formatNumber = value => {
    if (value === null || value === undefined || value === "") {
      return "0";
    }

    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
      return "0";
    }

    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: Number.isInteger(numericValue) ? 0 : 1,
      maximumFractionDigits: 1,
    }).format(numericValue);
  };

  const formatPercentage = formatDelta;

  const transformOrganizationJobsData = apiData => {
    return [
      {
        title: "Total tasks this year",
        amount: formatNumber(apiData?.total_jobs?.this_year ?? 0),
        delta:
          apiData?.total_jobs?.percentage_change !== undefined &&
          apiData?.total_jobs?.percentage_change !== null
            ? formatPercentage(apiData.total_jobs.percentage_change)
            : null,
        deltaType:
          apiData?.total_jobs?.percentage_change !== undefined &&
          apiData?.total_jobs?.percentage_change !== null
            ? getDeltaType(apiData.total_jobs.percentage_change)
            : null,
        subText: "this year",
      },
      {
        title: `Total tasks this month (${
          getCurrentAndLastMonthYear().current_month
        })`,
        amount: formatNumber(apiData?.total_jobs_month?.this_month ?? 0),
        delta:
          apiData?.total_jobs_month?.percentage_change !== undefined &&
          apiData?.total_jobs_month?.percentage_change !== null
            ? formatPercentage(apiData.total_jobs_month.percentage_change)
            : null,
        deltaType:
          apiData?.total_jobs_month?.percentage_change !== undefined &&
          apiData?.total_jobs_month?.percentage_change !== null
            ? getDeltaType(apiData.total_jobs_month.percentage_change)
            : null,
        subText: "this month",
      },
      {
        title: "Total missed task this year",
        amount: formatNumber(apiData?.missed_jobs?.this_year ?? 0),
        delta:
          apiData?.missed_jobs?.percentage_change !== undefined &&
          apiData?.missed_jobs?.percentage_change !== null
            ? formatPercentage(apiData.missed_jobs.percentage_change)
            : null,
        deltaType:
          apiData?.missed_jobs?.percentage_change !== undefined &&
          apiData?.missed_jobs?.percentage_change !== null
            ? getDeltaType(apiData.missed_jobs.percentage_change)
            : null,
        subText: "this year",
      },

      //   right side cards
      {
        title: `Total task last year (${
          getCurrentAndLastMonthYear().last_year
        })`,
        amount: formatNumber(apiData?.total_jobs_last_year ?? 0),
        delta: null,
        deltaType: null,
        subText: null,
      },
      {
        title: "Avg tasks per month",
        amount: formatNumber(apiData?.average_jobs_month ?? 0),
        delta:
          apiData?.average_jobs_month?.percentage_change !== undefined &&
          apiData?.average_jobs_month?.percentage_change !== null
            ? formatPercentage(apiData.average_jobs_month.percentage_change)
            : null,
        deltaType:
          apiData?.average_jobs_month?.percentage_change !== undefined &&
          apiData?.average_jobs_month?.percentage_change !== null
            ? getDeltaType(apiData.average_jobs_month.percentage_change)
            : null,
        subText: "this year",
      },
      {
        title: "Average missed task per month",
        amount: formatNumber(
          apiData?.average_missed_jobs_month?.up_to_this_month ?? 0,
        ),
        delta:
          apiData?.average_missed_jobs_month?.percentage_change !== undefined &&
          apiData?.average_missed_jobs_month?.percentage_change !== null
            ? formatPercentage(
                apiData.average_missed_jobs_month.percentage_change,
              )
            : null,
        deltaType:
          apiData?.average_missed_jobs_month?.percentage_change !== undefined &&
          apiData?.average_missed_jobs_month?.percentage_change !== null
            ? getDeltaType(apiData.average_missed_jobs_month.percentage_change)
            : null,
        subText: "this month",
      },
    ];
  };

  useEffect(() => {
    if (selectedOrganization?.id && getDashboardOrganizationJobs) {
      getDashboardOrganizationJobs({
        organization_id: selectedOrganization.id,
      });
    }
  }, [selectedOrganization, getDashboardOrganizationJobs]);

  useEffect(() => {
    setCardsData(transformOrganizationJobsData(organizationJobsData || {}));
  }, [organizationJobsData]);

  useEffect(() => {
    const measureCards = () => {
      const heights = cardRefs.current
        .map(card => card?.getBoundingClientRect().height || 0)
        .filter(Boolean);

      if (!heights.length) {
        return;
      }

      const tallestHeight = Math.ceil(Math.max(...heights));
      setMaxCardHeight(previousHeight =>
        previousHeight === tallestHeight ? previousHeight : tallestHeight,
      );
    };

    measureCards();

    const resizeObserver = new ResizeObserver(() => {
      measureCards();
    });

    cardRefs.current.forEach(card => {
      if (card) {
        resizeObserver.observe(card);
      }
    });

    window.addEventListener("resize", measureCards);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", measureCards);
    };
  }, [cardsData, organizationJobsIsLoading]);

  const registerCardRef = index => element => {
    cardRefs.current[index] = element;
  };

  return (
    <DashBoardCommonGrid
      responsiveBreakpoint="1470px"
      responsiveColumns="1fr"
      columns="1fr 1fr"
      gap="20px"
    >
      <CommonGrid columns="1fr" gap="20px">
        <CompleteJobCompareChart
          getDashboardCompleteJobCompare={getDashboardCompleteJobCompare}
          completeJobCompareIsLoading={completeJobCompareIsLoading}
          selectedOrganization={selectedOrganization}
          lastYearCompleteJobCompare={lastYearCompleteJobCompare}
          thisYearCompleteJobCompare={thisYearCompleteJobCompare}
          currentMonthIndex={currentMonthIndex}
        />
      </CommonGrid>

      <CommonGrid columns="1fr 1fr" gap="20px">
        <CommonGrid columns="1fr" gap="12px">
          {cardsData.slice(0, 3).map((item, index) => (
            <SmallInfoCard
              key={index}
              ref={registerCardRef(index)}
              title={item.title}
              amount={item.amount}
              delta={item.delta}
              deltaType={item.deltaType}
              subText={item.subText}
              isLoading={organizationJobsIsLoading}
              cardHeight={maxCardHeight ? `${maxCardHeight}px` : undefined}
            />
          ))}
        </CommonGrid>

        <CommonGrid columns="1fr" gap="12px">
          {cardsData.slice(3, 6).map((item, index) => (
            <SmallInfoCard
              key={index + 3}
              ref={registerCardRef(index + 3)}
              title={item.title}
              amount={item.amount}
              delta={item.delta}
              deltaType={item.deltaType}
              subText={item.subText}
              isLoading={organizationJobsIsLoading}
              cardHeight={maxCardHeight ? `${maxCardHeight}px` : undefined}
            />
          ))}
        </CommonGrid>
      </CommonGrid>
    </DashBoardCommonGrid>
  );
};

export default DashBoardOperationThirdSection;
